import { useCallback, useEffect, useMemo, useState } from "react";
import type { ReactNode } from "react";
import { api, setUnauthorizedHandler } from "../../api/client";
import { AuthContext } from "./useAuth";
import type { AuthState, AuthStatus } from "./useAuth";
import type { LoginRequest, User } from "../../api/types";
import { applyTheme } from "../../theme/theme";

// 提供登入狀態給整棵樹。開頁先問一次 /api/auth/me,並接住之後任何一支
// API 呼叫回來的 401 —— session 中途過期也要能回到登入頁。

export interface AuthProviderProps {
  children: ReactNode;
}

export function AuthProvider({ children }: AuthProviderProps) {
  const [user, setUser] = useState<User | null>(null);
  const [status, setStatus] = useState<AuthStatus>("loading");

  const forgetSession = useCallback(() => {
    setUser(null);
    setStatus("anonymous");
  }, []);

  // 帳號上存的主題說了算(業主 2026-09-25)。登入、開頁、重新讀一次 /auth/me
  // 都走這裡;沒選過(null)就回到跟著系統設定。登出不動它 —— 登入頁照舊
  // 跟著系統設定是 tokens.css 的事,這裡只負責「已經知道是誰」的時候。
  const adopt = useCallback((me: User) => {
    setUser(me);
    applyTheme(me.theme ?? null);
  }, []);

  // 先掛好 401 的接手人,再去問「我是誰」,免得第一發 401 沒人接。
  useEffect(() => {
    setUnauthorizedHandler(forgetSession);
    return () => setUnauthorizedHandler(null);
  }, [forgetSession]);

  useEffect(() => {
    let cancelled = false;
    api
      .get<User>("/auth/me")
      .then((me) => {
        if (cancelled) return;
        adopt(me);
        setStatus("authenticated");
      })
      .catch(() => {
        // 401 代表還沒登入,不是故障;其他錯誤也一樣當未登入處理,
        // 至少使用者看得到登入表單,而不是一片空白。
        if (!cancelled) forgetSession();
      });
    return () => {
      cancelled = true;
    };
  }, [forgetSession, adopt]);

  const login = useCallback(async (email: string, password: string, remember = false) => {
    const payload: LoginRequest = { email, password, remember };
    const me = await api.post<User>("/auth/login", payload);
    adopt(me);
    setStatus("authenticated");
  }, [adopt]);

  const logout = useCallback(async () => {
    try {
      await api.post("/auth/logout");
    } finally {
      // 後端沒回應也要在前端登出。否則使用者按了登出卻還留在原地,
      // 會以為自己已經離開了。
      forgetSession();
    }
  }, [forgetSession]);

  const refresh = useCallback(async () => {
    adopt(await api.get<User>("/auth/me"));
  }, [adopt]);

  const value = useMemo<AuthState>(
    () => ({ status, user, login, logout, refresh }),
    [status, user, login, logout, refresh],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}
